'use client';

import React from 'react';
import { motion } from 'motion/react';
import Image from '@/components/Image';
import { TUTOR_CHARACTERS } from './tutorPersonalities';

interface CharacterPickerProps {
  selectedId: string;
  onSelect: (personalityId: string) => void;
  onClose: () => void;
}

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.04,
    },
  },
};

const itemVariants = {
  hidden: { scale: 0, opacity: 0, y: 12 },
  visible: {
    scale: 1,
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 560,
      damping: 17,
      mass: 0.65,
    },
  },
};

export function CharacterPicker({ selectedId, onSelect, onClose }: CharacterPickerProps) {
  return (
    <motion.div
      className="grid grid-cols-4 gap-1 p-2"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {TUTOR_CHARACTERS.map((char) => {
        const isSelected = char.personalityId === selectedId;
        return (
          <motion.button
            key={char.personalityId}
            type="button"
            variants={itemVariants}
            onClick={() => { onSelect(char.personalityId); onClose(); }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.93 }}
            className={`flex flex-col items-center gap-1 p-1.5 rounded-xl ${
              isSelected
                ? 'ring-2 ring-[color:var(--primary-500)] bg-[color:var(--primary-50)]'
                : 'hover:bg-[color:var(--surface-1)]'
            }`}
            title={char.name}
          >
            <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
              <Image
                src={char.avatar}
                alt={char.name}
                width={40}
                height={40}
                optimizedWidth={200}
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-[10px] text-[color:var(--gray-600)] font-medium truncate w-full text-center leading-none">
              {char.name}
            </span>
          </motion.button>
        );
      })}
    </motion.div>
  );
}
